import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import SiteNavbar from '../../../components/feature/SiteNavbar';
import FooterSection from '../../home/components/FooterSection';

interface LegalSection {
  title: string;
  content: ReactNode;
}

interface LegalLayoutProps {
  title: string;
  subtitle: string;
  updated: string;
  sections: LegalSection[];
  navLinks: { label: string; href: string }[];
}

export default function LegalLayout({ title, subtitle, updated, sections, navLinks }: LegalLayoutProps) {
  return (
    <div className="bg-zinc-950 min-h-screen">
      <SiteNavbar />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-zinc-950 border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-3.5 py-1.5 mb-5">
            <i className="ri-file-text-line text-orange-400 text-sm" />
            <span className="text-zinc-400 text-xs font-semibold">Právne dokumenty</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-3 leading-tight">{title}</h1>
          <p className="text-zinc-500 text-base mb-4">{subtitle}</p>
          <p className="text-zinc-600 text-xs">
            <i className="ri-refresh-line mr-1" />
            Posledná aktualizácia: <span className="text-zinc-500">{updated}</span>
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sidebar nav */}
          <aside className="lg:w-56 flex-shrink-0">
            <div className="lg:sticky lg:top-28">
              <p className="text-zinc-600 text-xs uppercase tracking-widest font-semibold mb-4">Ďalšie dokumenty</p>
              <nav className="flex flex-row lg:flex-col gap-2">
                {navLinks.map((l) => (
                  <Link
                    key={l.href}
                    to={l.href}
                    className="text-zinc-500 hover:text-orange-400 text-sm py-1.5 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>

              <div className="mt-8 hidden lg:block border-t border-zinc-800/60 pt-6">
                <p className="text-zinc-600 text-xs mb-3">Otázky k dokumentom?</p>
                <a
                  href="mailto:info@boostio.sk"
                  className="inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-sm transition-colors duration-200"
                >
                  <i className="ri-mail-line" />
                  info@boostio.sk
                </a>
              </div>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <div className="space-y-10">
              {sections.map((s, i) => (
                <article key={i} className="border-b border-zinc-800/50 pb-10 last:border-0">
                  <h2 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
                    <span className="text-orange-500 text-sm font-black opacity-60">{String(i + 1).padStart(2, '0')}</span>
                    {s.title}
                  </h2>
                  <div className="text-zinc-400 text-sm leading-relaxed space-y-3">{s.content}</div>
                </article>
              ))}
            </div>

            {/* Bottom contact note */}
            <div className="mt-14 bg-zinc-900/50 border border-zinc-800/50 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-500/10 border border-orange-500/20 rounded-xl flex-shrink-0">
                <i className="ri-question-line text-orange-400 text-lg" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm mb-0.5">Máte otázky k týmto podmienkam?</p>
                <p className="text-zinc-500 text-xs">Kontaktujte nás na <a href="mailto:info@boostio.sk" className="text-orange-400 hover:underline">info@boostio.sk</a> - odpovieme do 24 hodín.</p>
              </div>
            </div>
          </main>
        </div>
      </div>

      <FooterSection />
    </div>
  );
}
